import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorSettings } from "../../services/helper";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner,
  Container,
  Card,
  Input,
  Button,
  Row,
  Col,
  CardBody,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  FaBoxes,
  FaDollarSign,
  FaWarehouse,
  FaChartBar,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";
import classnames from "classnames";

const StockReport = () => {
  const api_url = getAPIURL();
  const vendor_id = getVendorID();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchStockReport();
  }, []);

  const printReport = () => {
    const vendor_email = getVendorSettings("email");
    const vendor_store_name = getVendorSettings("name");
    const vendor_address = getVendorSettings("vendor_contact_address");
    const vendor_phone = getVendorSettings("vendor_contact_phone");
    
    const totalProducts = data.length;
    const inStockItems = data.filter(item => item.stock_status === "instock").length;
    const outOfStockItems = data.filter(item => item.stock_status === "outofstock").length;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Stock Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0; }
            .header p { margin: 0; }
            .header h3 { margin-top: 20px; margin-bottom: 10px; }
            .header hr { margin-top: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${vendor_store_name}</h2>
            <p>${vendor_address}</p>
            <p>${vendor_phone} | ${vendor_email}</p>
            <h3>Stock Report</h3>
            <hr>
          </div>
          <div class="summary">
            <p><strong>Total Products:</strong> ${totalProducts}</p>
            <p><strong>In Stock Items:</strong> ${inStockItems}</p>
            <p><strong>Out of Stock Items:</strong> ${outOfStockItems}</p>
            <p><strong>Total Quantity:</strong> ${data.reduce((sum, item) => {
              const qty = isNaN(Number(item.stock_quantity)) ? 0 : Number(item.stock_quantity);
              return sum + qty;
            }, 0)}</p>
            <p><strong>Total Value:</strong> $${data.reduce((sum, item) => {
              const qty = isNaN(Number(item.stock_quantity)) ? 0 : Number(item.stock_quantity);
              return sum + (qty * Number(item.price || 0));
            }, 0).toFixed(2)}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Product ID</th>
                <th>Product Name</th>
                <th>SKU</th>
                <th>Stock Quantity</th>
                <th>Stock Status</th>
                <th>Price</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              ${data.filter(item => 
                item.product_name.toLowerCase().includes(search.toLowerCase())
              ).map(item => `
                <tr>
                  <td>${item.product_id}</td>
                  <td>${item.product_name}</td>
                  <td>${item.sku || 'N/A'}</td>
                  <td>${item.stock_quantity ?? 'N/A'}</td>
                  <td>${(() => {
                    if (item.stock_status) {
                      return item.stock_status === "instock" ? "In Stock" : "Out of Stock";
                    }
                    const qty = Number(item.stock_quantity);
                    return isNaN(qty) ? "Unknown" : qty > 0 ? "In Stock" : "Out of Stock";
                  })()}</td>
                  <td>$${Number(item.price || 0).toFixed(2)}</td>
                  <td>$${(() => {
                    const qty = isNaN(Number(item.stock_quantity)) ? 0 : Number(item.stock_quantity);
                    return (qty * Number(item.price || 0)).toFixed(2);
                  })()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const fetchStockReport = async () => {
    setLoading(true);
    const params = new URLSearchParams({ vendor_id });
    try {
      const { data } = await http.get(
        `${api_url}/reports/stock?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching stock report:", error);
    }
    setLoading(false);
  };

  const columns = [
    { name: "Product ID", selector: (row) => row.product_id, sortable: true },
    {
      name: "Product Name",
      selector: (row) => row.product_name,
      sortable: true,
    },
    { name: "SKU", selector: (row) => row.sku || "N/A", sortable: true },
    {
      name: "Stock Quantity",
      selector: (row) => row.stock_quantity ?? "N/A",
      sortable: true,
    },
    {
      name: "Stock Status",
      selector: (row) => {
        if (row.stock_status) {
          return row.stock_status === "instock" ? "In Stock" : "Out of Stock";
        }
        const qty = Number(row.stock_quantity);
        return isNaN(qty) ? "Unknown" : qty > 0 ? "In Stock" : "Out of Stock";
      },
      cell: (row) => {
        let status;
        if (row.stock_status) {
          status = row.stock_status === "instock" ? "In Stock" : "Out of Stock";
        } else {
          const qty = Number(row.stock_quantity);
          status = isNaN(qty) ? "Unknown" : qty > 0 ? "In Stock" : "Out of Stock";
        }
        return (
          <span style={{ color: status === "Out of Stock" ? "red" : "inherit" }}>
            {status}
          </span>
        );
      },
      sortable: true,
    },
    { name: "Price", selector: (row) => `$${Number(row.price || 0).toFixed(2)}`, sortable: true },
    {
      name: "Total Value",
      selector: (row) => {
        const qty = isNaN(Number(row.stock_quantity)) ? 0 : Number(row.stock_quantity);
        return `$${(qty * Number(row.price || 0)).toFixed(2)}`;
      },
      sortable: true,
    },
  ];

  const totalProducts = data.length;
  const inStockItems = data.filter(item => item.stock_status === "instock").length;
  const outOfStockItems = data.filter(item => item.stock_status === "outofstock").length;
  const totalQuantity = data.reduce((sum, item) => {
    const qty = isNaN(Number(item.stock_quantity)) ? 0 : Number(item.stock_quantity);
    return sum + qty;
  }, 0);
  const totalValue = data.reduce((sum, item) => {
    const qty = isNaN(Number(item.stock_quantity)) ? 0 : Number(item.stock_quantity);
    return sum + (qty * Number(item.price || 0));
  }, 0);

  const filteredData = data.filter((item) =>
    item.product_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-body">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-center flex-grow-1">Stock Report</h2>
        </div>

        {/* Search Filter */}
        <div className="d-flex mb-3 gap-2">
          <Input
            type="text"
            placeholder="Search by product name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button color="primary" onClick={fetchStockReport}>
            Refresh
          </Button>
          <Button
            color="info"
            onClick={() => {
              setSearch("");
              fetchStockReport();
            }}
          >
            Reset
          </Button>
        </div>

        {/* Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "summary" })}
              onClick={() => setActiveTab("summary")}
              style={{ cursor: "pointer" }}
            >
              Summary
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "listing" })}
              onClick={() => setActiveTab("listing")}
              style={{ cursor: "pointer" }}
            >
              Listing
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="summary">
            {loading ? (
              <Spinner color="primary" />
            ) : (
              <>
                <Row className="mb-4">
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaBoxes size={30} className="text-primary mb-2" />
                      <CardTitle tag="h5">Total Products</CardTitle>
                      <h4>{totalProducts}</h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaWarehouse size={30} className="text-success mb-2" />
                      <CardTitle tag="h5">In Stock Items</CardTitle>
                      <h4>{inStockItems}</h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaChartBar size={30} className="text-danger mb-2" />
                      <CardTitle tag="h5">Out of Stock</CardTitle>
                      <h4>{outOfStockItems}</h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaWarehouse size={30} className="text-warning mb-2" />
                      <CardTitle tag="h5">Total Quantity</CardTitle>
                      <h4>{totalQuantity}</h4>
                    </CardBody>
                  </Card>
                </Col>
                </Row>
                <Row className="mb-4">
                <Col md="3">
                  <Card className="p-3 shadow-sm text-center">
                    <CardBody>
                      <FaDollarSign size={30} className="text-info mb-2" />
                      <CardTitle tag="h5">Total Value</CardTitle>
                      <h4>${totalValue.toFixed(2)}</h4>
                    </CardBody>
                  </Card>
                </Col>
                </Row>
              </>
            )}
          </TabPane>

          <TabPane tabId="listing">
            {loading ? (
              <div className="text-center">
                <Spinner color="primary" size="lg" />
                <p>Loading Stock Data...</p>
              </div>
            ) : (
              <Card className="p-3 shadow-sm">
                <h4 className="mb-3">Stock Inventory</h4>
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  striped
                  highlightOnHover
                />
              </Card>
            )}
          </TabPane>
        </TabContent>
        
        <div className="text-center mt-4 mb-4">
          <Button color="success" onClick={printReport}>
            Print Report
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default StockReport;
